import { EventContextProvider } from "@codat/analytics";
import { useGetClientEnabledQuery } from "api/queries/useGetClientEnabledQuery";
import { useCompanies } from "contexts/CompaniesContext";
import { useGetCompany } from "hooks/query/useGetCompany";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useEffect, useMemo, useState } from "react";
import { Redirect, Switch, useParams } from "react-router-dom";
import { Subject } from "rxjs";

import { paths } from "components/App/paths";
import AuthorizedRoute from "components/AuthorizedRoute";
import { CompanyHeader } from "components/CompanyHeader";
import Page from "components/Page/Page";
import TrackedRoute from "components/TrackedRoute";
import Accounting from "pages/Accounting";
import Assess from "pages/Assess";
import Banking from "pages/Banking";
import Commerce from "pages/Commerce";
import { ConnectionManagement } from "pages/ConnectionManagement";
import DataHistory from "pages/DataHistory";
import Files from "pages/Files";
import Summary from "pages/Summary";

import CompaniesSidebarGroup from "./companiesSidebarGroup";
import { findDataConnectionId } from "./utils/findDataConnectionId";

export interface ConnectionIds {
  commerceConnectionId: string | undefined;
  accountingConnectionId: string | undefined;
}

const CompaniesRoutedPage = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const { setActiveCompany } = useCompanies();
  const { data, isLoading, isFetching } = useGetCompany({ companyId });

  const [dataConnectionsIds, setDataConnectionsIds] = useState<ConnectionIds>({
    commerceConnectionId: undefined,
    accountingConnectionId: undefined,
  });

  const [
    { data: visualizeEnabled, loading: visualizeEnabledLoading },
    checkClientEnabled,
  ] = useGetClientEnabledQuery();

  const queueSync$ = useMemo(() => new Subject<void>(), []);

  useEffect(() => {
    if (isFetching) {
      setActiveCompany(null);
    }
    if (data) {
      setActiveCompany(data);
    }
  }, [data, isFetching, setActiveCompany]);

  useEffect(() => {
    if (data?.dataConnections?.length) {
      checkClientEnabled().catch(() => {
        // error is returned in query above but a catch is needed here as calling manually
      });
    }
  }, [data, checkClientEnabled]);

  const { userState } = useUserState();

  const sidebarGroups = CompaniesSidebarGroup(
    companyId,
    !!visualizeEnabled,
    dataConnectionsIds,
    userState?.userPermissions
  );

  useEffect(() => {
    setDataConnectionsIds({
      commerceConnectionId: findDataConnectionId(
        "Commerce",
        data?.dataConnections
      ),
      accountingConnectionId: findDataConnectionId(
        "Accounting",
        data?.dataConnections
      ),
    });
  }, [data?.dataConnections]);

  return (
    <Page
      header={
        <EventContextProvider name="Company Summary Header">
          <CompanyHeader
            accountingDataConnection={
              dataConnectionsIds?.accountingConnectionId
            }
            companyId={companyId}
            companyData={data}
            loading={isLoading}
            queueSync$={queueSync$}
          />
        </EventContextProvider>
      }
      sidebarGroupsOverride={sidebarGroups}
      sidebarLoading={isLoading || visualizeEnabledLoading}
    >
      <Switch>
        <Redirect
          from={paths.companies.ACCOUNTING_API}
          to={paths.companies.ACCOUNTING_DATA}
        />
        <Redirect
          from={paths.companies.BANKING_API}
          to={paths.companies.BANKING_DATA}
        />
        <Redirect
          from={paths.companies.COMMERCE_API}
          to={paths.companies.COMMERCE_DATA}
        />
        <AuthorizedRoute
          exact
          path={paths.companies.SUMMARY}
          permission="data_all_view"
          render={() => <Summary companyId={companyId} />}
        />
        <TrackedRoute
          path={paths.companies.CONNECTIONS}
          name="Manage connections"
          component={ConnectionManagement}
        />
        <AuthorizedRoute
          path={paths.companies.ASSESS}
          permission="data_all_view"
          render={() => <Assess />}
        />
        <AuthorizedRoute
          exact
          path={paths.companies.FILES}
          permission="data_all_view"
          render={() => <Files />}
        />
        <AuthorizedRoute
          exact
          path={paths.companies.ACCOUNTING_DATA}
          permission="data_all_view"
          render={() => <Accounting companyId={companyId} />}
        />
        <AuthorizedRoute
          exact
          path={paths.companies.BANKING_DATA}
          permission="data_all_view"
          render={() => <Banking companyId={companyId} />}
        />
        <AuthorizedRoute
          exact
          path={paths.companies.COMMERCE_DATA}
          permission="data_all_view"
          render={() => <Commerce companyId={companyId} />}
        />
        <AuthorizedRoute
          exact
          path={paths.companies.READ_HISTORY}
          permission="data_all_view"
          component={() => <DataHistory queueSync$={queueSync$} />}
        />
        <AuthorizedRoute
          exact
          path={paths.companies.WRITE_HISTORY}
          permission="data_all_view"
          component={() => <DataHistory queueSync$={queueSync$} />}
        />
        <Redirect
          from={paths.companies.DATASETS}
          to={paths.companies.READ_HISTORY}
        />
      </Switch>
    </Page>
  );
};

export default CompaniesRoutedPage;
